<script>
import { defineComponent, inject, ref, computed, onMounted, watch } from 'vue'
import ReadinessPanel from "./ReadinessPanel.vue";
import cUserFeedback from "@/components/cUserFeedback.vue";
import nlpUtils from "@/common/nlpUtils.js";
import { useDocReadinessCheck } from "@/composables/useDocReadinessCheck";
import { anchorIntroNote } from '@rm-ui/levelling/levellingMeta';
import axios from "axios"

export default defineComponent({
  name: 'EvaluateNew',
  components: {
    ReadinessPanel,
    cUserFeedback
  },
  props: {
    doc: {
      type: Object,
      required: true
    },
    task: {
      type: Object,
      required: false
    },
    isNew: {
      type: Boolean,
      required: false,
      default: false
    },
    /* Has this role been levelled before? Gates the re-evaluate intro: an existing
       job document is not the same thing as a previous evaluation, and on a role
       being levelled for the first time that step promised pre-filled answers that
       do not exist. */
    hasPreviousEvaluation: {
      type: Boolean,
      required: false,
      default: false
    },
    confirmMessage: {
      type: String,
      required: false,
      default: null
    },
    // Framework factors ({ id, name, description, weighting, subFactors[] }) —
    // used by the advisory JD readiness check (PD-1250).
    factors: {
      type: Array,
      required: false,
      default: () => []
    },
    // Plain text of the RM system document, used as the readiness source when
    // no file has been uploaded (the "Check the job document" screen).
    jdSystemText: {
      type: String,
      required: false,
      default: ''
    },
    // Readiness prompt-chain id; null/absent disables the check.
    readinessChainId: {
      type: [Number, String],
      required: false,
      default: null
    },
    readinessThresholds: {
      type: Object,
      required: false,
      default: () => ({})
    },
    /* Anchor context for the intro note (PD-1258) — the journey says this before the
       first question because rationale quality matters more on a calibration
       reference than anywhere else. */
    /* Job family, resolved by the holder from the anchor record — the journey's doc
       payload carries none, which is why the note used to say "other roles in its
       family" for every anchor. */
    anchorFamily: {
      type: String,
      required: false,
      default: null,
    },
    anchorIntent: {
      type: Boolean,
      required: false,
      default: false
    },
    isAnchor: {
      type: Boolean,
      required: false,
      default: false
    },
    anchorPending: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['uploadSkipped', 'fileUploaded', 'error', 'sessionExpired', 'viewState', 'preview', 'back'],
  setup(props, { emit }) {
    const loginState = inject('loginState')
    const store = inject('store')
    const viewState = ref('welcome');
    /* Shared copy, so beta reads the same three variants. */
    const anchorIntro = computed(() => anchorIntroNote({
      intent: props.anchorIntent,
      isAnchor: props.isAnchor,
      pending: props.anchorPending,
      family: props.anchorFamily || (props.doc && props.doc.family) || null,
    }))
    // Surface the internal step to the holder so it can show its shared job-document
    // preview column alongside the "Confirm the role and document" / "Add the role" steps.
    watch(viewState, (v) => emit('viewState', v), { immediate: true })
    const fileUploadFormData = ref({})
    const applicableHierarchyTypes = ref([])
    const fileInput = ref(null)

    // Accepted upload types (drag/drop bypasses the <input accept> filter, so we
    // validate against this list ourselves).
    const acceptedExtensions = ['.doc', '.docx', '.pdf']
    const isDragging = ref(false)
    // Tracks dragenter/dragleave depth so moving over child elements doesn't
    // flicker the highlight off.
    let dragDepth = 0

    // --- Upload preview / confirmation state ---
    const previewFile = ref(null)
    const previewContentHtml = ref('')
    const previewLoading = ref(false)
    // Guards against a stale poll (for a superseded file) writing back its result
    // once the user has moved on to a different file.
    let previewToken = 0

    const previewFileName = computed(() =>
      previewFile.value?.file_data?.file_name
        || previewFile.value?.file_name
        || props.doc?.doc_name
        || 'Uploaded document'
    )

    // Feed the extracted preview to the holder so it can render it in the shared
    // right-hand column instead of inline. Fires as the text streams in.
    watch([previewFileName, previewContentHtml, previewLoading], () => {
      emit('preview', {
        fileName: previewFileName.value,
        contentHtml: previewContentHtml.value,
        loading: previewLoading.value,
      })
    })
    // --- JD readiness check (PD-1250, advisory) ---
    const readiness = useDocReadinessCheck()

    function stripHtml(html) {
      if (!html) return ''
      const div = document.createElement('div')
      div.innerHTML = html
      return (div.textContent || '').trim()
    }

    // Fire the readiness check against the given source text. No-op unless a
    // chain id is configured and factors + text are present (guarded in run()).
    function runReadiness(text) {
      if (!props.readinessChainId) return
      readiness.run({
        getText: () => text,
        factors: props.factors,
        chainId: props.readinessChainId,
        systemReference: props.doc?.system_number,
        thresholds: props.readinessThresholds,
      })
    }

    // Existing job document ("Check the job document"): source is the RM system
    // document text, which loads asynchronously in the parent — re-fire when it
    // becomes available while the docPreview screen is showing.
    watch(
      () => [viewState.value, props.jdSystemText],
      () => {
        if (
          viewState.value === 'docPreview' &&
          props.jdSystemText &&
          !readiness.hasResult.value &&
          !readiness.running.value
        ) {
          runReadiness(props.jdSystemText)
        }
      },
      { immediate: true }
    )

    onMounted(() => {
      initialize()
    })

    async function initialize() {
      viewState.value = 'welcome';
      if (!props.isNew && props.doc?.doc_id) {
        /* Re-evaluating an existing job document. The prototype opens on an INTRO —
           what is happening and why — and confirms the document as a separate step
           after it. We used to merge the two, which buried "you are re-evaluating this
           role" under a file card and a readiness check.
           A role being levelled for the FIRST time (an existing job document with no
           completed evaluation) has nothing to re-evaluate, so it goes straight to the
           document step. */
        viewState.value = props.hasPreviousEvaluation ? 'reIntro' : 'docPreview'
        return
      }
      if (props.doc && !props.task)
        start()
    }

    function start() {
      fileUploadFormData.value = {
        link_doc_id: props.doc.doc_id
      }
      viewState.value = 'upload';
    }

    function skipUpload() {
      emit('uploadSkipped');
    }

    // Show the confirmation preview for a freshly-uploaded (or already-attached)
    // file, then fetch its extracted text. Extraction is asynchronous, so the
    // text may not be ready immediately — fetchPreviewText polls for it.
    async function showPreview(file) {
      previewFile.value = file
      viewState.value = 'preview'
      readiness.reset()
      if (file.content) {
        previewContentHtml.value = file.content
        runReadiness(stripHtml(file.content))
        return
      }
      previewContentHtml.value = ''
      previewLoading.value = true
      await fetchPreviewText(file)
    }

    async function fetchPreviewText(file) {
      const token = ++previewToken
      const maxAttempts = 5
      const delayMs = 1500
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const text = await nlpUtils.getExtractedFileText(file.file_id, file.row_id ?? null)
        if (token !== previewToken) return // superseded by a newer preview
        if (text) {
          previewContentHtml.value = text
          previewLoading.value = false
          runReadiness(stripHtml(text))
          return
        }
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs))
          if (token !== previewToken) return
        }
      }
      // Give up quietly: JdPreview shows a "not ready yet" note and the user can
      // still confirm by filename and review the JD during the evaluation.
      previewLoading.value = false
    }

    function useThisFile() {
      if (previewFile.value)
        emit('fileUploaded', {
          ...previewFile.value,
          content: previewContentHtml.value
        })
    }

    // Open the OS file picker directly (no intermediate dialog). Also cancels any
    // in-flight preview poll for the file currently being replaced.
    function openFilePicker() {
      previewToken++
      fileInput.value?.click()
    }

    function isAcceptedFile(file) {
      const name = (file?.name || '').toLowerCase()
      return acceptedExtensions.some(ext => name.endsWith(ext))
    }

    // Upload a file immediately, then hand off to the existing
    // filesUploadedCallback flow (link-to-doc + preview) exactly as before.
    async function uploadFile(file) {
      if (!file) return
      if (!isAcceptedFile(file)) {
        emit('error', 'Please upload a PDF or Word document (.pdf, .doc, .docx).')
        return
      }

      const prevViewState = viewState.value
      viewState.value = 'loading'

      const formData = new FormData()
      formData.append('files[]', file)
      formData.append('private', false)
      formData.append('hierarchies', JSON.stringify([]))
      if (fileUploadFormData.value.link_doc_id !== undefined) {
        formData.append('link_doc_id', fileUploadFormData.value.link_doc_id)
      }

      try {
        const resp = await axios.post('/file/uploadMulti', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        await filesUploadedCallback(resp.data)
      } catch (err) {
        if (err.response && err.response.status === 401) {
          emit('sessionExpired', err)
        } else {
          console.log(err)
          emit('error', err.response?.data || 'Error uploading file')
        }
        viewState.value = prevViewState
      }
    }

    async function onFileSelected(event) {
      previewToken++ // cancel any in-flight poll for the file being replaced
      const file = event.target.files?.[0]
      // Reset so re-selecting the same file still fires @change.
      event.target.value = ''
      await uploadFile(file)
    }

    function onDragOver() {
      isDragging.value = true
    }

    function onDragEnter() {
      dragDepth++
      isDragging.value = true
    }

    function onDragLeave() {
      dragDepth = Math.max(0, dragDepth - 1)
      if (dragDepth === 0) isDragging.value = false
    }

    async function onDrop(event) {
      dragDepth = 0
      isDragging.value = false
      previewToken++ // cancel any in-flight poll for the file being replaced
      const file = event.dataTransfer?.files?.[0]
      await uploadFile(file)
    }

    async function linkFileToDoc(docData) {
      if (docData.length === 0)
        emit('error', 'Error creating document');

      viewState.value = 'loading';
      await Promise.all(docData.map(async file => {
        let data = {
          doc_id: props.doc.doc_id,
          file_id: file.file_id,
        };

        await axios
          .post("file/linkFileToDoc/", data)
          .catch((err) => {
            if (err.response && err.response.status === 401) {
              emit("sessionExpired", err);
            } else {
              console.log(err);
              emit('error', err.response?.data || 'Error creating document');
            }
          });
      }));

      // Show the preview/confirmation instead of advancing straight away — the
      // flow only continues once the user confirms via "Use this job description".
      showPreview(docData[0])
    }

    async function filesUploadedCallback(data) {
      store.dispatch("files/reload");
      let responseData = data[0];
      if (responseData.newDocuments && responseData.newDocuments.length > 1) {
        //doc already created
        //clearFilters();
        emit('docCreated', responseData.newDocuments[responseData.newDocuments.length - 1]);
      } else {
        await linkFileToDoc(data);
      }
    }

    return {
      anchorIntro,
      loginState,
      viewState,
      start,
      skipUpload,
      applicableHierarchyTypes,
      fileUploadFormData,
      filesUploadedCallback,
      fileInput,
      onFileSelected,
      isDragging,
      onDragOver,
      onDragEnter,
      onDragLeave,
      onDrop,
      previewFile,
      previewFileName,
      previewContentHtml,
      previewLoading,
      useThisFile,
      openFilePicker,
      readiness,
    }
  }
})
</script>

<template>
  <div class="lvl-eval">
    <!-- Loading -->
    <div v-if="viewState === 'loading'" class="lvl-qcard">
      <div class="lvl-qcard-in lvl-wc-loading">
        <span class="ms lvl-wc-spin" style="font-size:44px">progress_activity</span>
        <p>Please wait&hellip;</p>
      </div>
    </div>

    <!-- Welcome -->
    <div v-else-if="viewState === 'welcome'" class="lvl-qcard">
      <div class="lvl-welcome">
        <div class="lvl-welcome-head">
          <span class="lvl-welcome-ic"><span class="ms" style="font-size:24px">fact_check</span></span>
          <h1 class="lvl-q-title">You have been invited to evaluate the role &OpenCurlyDoubleQuote;{{ doc?.doc_name }}&CloseCurlyDoubleQuote;</h1>
        </div>

        <p class="lvl-q-desc">
          This is a simple process where we ask a few questions to help understand the value of a role (also known as role levelling).
          For example: &ldquo;How much specialised knowledge does someone need to do this role?&rdquo;
        </p>

        <div class="lvl-note lvl-note--info">
          <span class="ms">info</span>
          <span>We&apos;ll guide you as you go. Just consider each question and answer as best you can.</span>
        </div>
      </div>

      <div class="lvl-cardfoot lvl-welcome-foot">
        <button type="button" class="lvl-btn" @click="start">Let's start <span class="ms">arrow_forward</span></button>
      </div>
    </div>

    <!-- Re-evaluate, step 1 of 2: WHAT is happening. A faithful port of the
         prototype's re-evaluate step — heading, the pre-filled explanation, the anchor
         context, and a single way onward. Deliberately no file card and no readiness
         check: those belong to the document step that follows, and having them here
         made this read as a form rather than an explanation.
         The holder's `showDocDetails` does not list this sub-state, so the shared
         right-hand JD column stays hidden and the card sits alone, as in the design. -->
    <div v-else-if="viewState === 'reIntro'" class="lvl-qcard lvl-welcome-card">
      <!-- Prototype structure exactly (LvlEvalWelcome): .lvl-welcome owns the padding
           and gap, .lvl-welcome-head sits the 46px icon badge beside the title, and
           .lvl-q-title / .lvl-q-desc carry the type. The generic .lvl-dochead used here
           before is the DOCUMENT-step head — it has no width cap and a different scale,
           which is why this card ran the full width of the column. -->
      <div class="lvl-welcome">
        <div class="lvl-welcome-head">
          <span class="lvl-welcome-ic"><span class="ms" style="font-size:24px">history</span></span>
          <h1 class="lvl-q-title">
            Re-evaluate the role &OpenCurlyDoubleQuote;{{ doc?.doc_name || 'this role' }}&CloseCurlyDoubleQuote;
          </h1>
        </div>

        <p class="lvl-q-desc">
          The previous answers for this role are pre-filled. Adjust anything that has changed, review
          the summary, and confirm &mdash; the current grade stays until you save.
        </p>

        <!-- Anchor context sits on THIS step only. It is about the role, not the
             document, and stating it twice across two steps reads as a warning. -->
        <div v-if="anchorIntro" class="lvl-note lvl-note--info lvl-anchor-intronote">
          <span class="ms">anchor</span>
          <span><strong>{{ anchorIntro.lead }}</strong> {{ anchorIntro.body }}</span>
        </div>
      </div>

      <div class="lvl-cardfoot lvl-welcome-foot">
        <button type="button" class="lvl-btn" @click="viewState = 'docPreview'">
          Let&apos;s start <span class="ms">arrow_forward</span>
        </button>
      </div>
    </div>

    <!-- Re-evaluate, step 2 of 2: confirm the document. The job document preview
         lives in the holder's shared right-hand column (as in later steps). -->
    <div v-else-if="viewState === 'docPreview'" class="lvl-qcard">
      <div class="lvl-qcard-in">
        <div class="lvl-dochead">
          <span class="lvl-welcome-ic"><span class="ms" style="font-size:24px">task</span></span>
          <div class="lvl-dochead-tx">
            <!-- The intro step above states what is happening; this one settles the
                 DOCUMENT the answers will be scored against, which often changes
                 between evaluations. -->
            <h2>Check the document</h2>
            <p>
              These answers will be scored against the job description below. Attach or replace it if the
              role itself has changed.
            </p>
          </div>
        </div>

        <div class="lvl-drop has lvl-wc-docfile" :class="{ 'lvl-drop--dragging': isDragging }" role="button" tabindex="0"
            @click="openFilePicker" @keydown.enter="openFilePicker" @keydown.space.prevent="openFilePicker"
            @dragenter.prevent="onDragEnter" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
          <span class="ms">task</span>
          <div class="lvl-wc-docfile-name">{{ doc.system_number || doc.doc_name }}</div>
          <button type="button" class="lvl-btn lvl-btn--outlined lvl-btn--sm">
            Replace
          </button>
        </div>

        <!-- Advisory JD readiness check (PD-1250). Hidden unless a readiness
             chain is configured for the client. -->
        <ReadinessPanel
          v-if="readinessChainId"
          :error="readiness.error.value"
          :summary="readiness.summary.value"
          :results="readiness.factorResults.value"
        >
          <!-- Is the advisory check actually worth running? (PD-1289) -->
          <template #feedback>
            <cUserFeedback
              feedback-type="LEVELLING_DOCUMENT_READINESS"
              :doc-id="doc?.doc_id"
              :prompt-chain-id="readinessChainId"
            />
          </template>
        </ReadinessPanel>
      </div>

      <div class="lvl-cardfoot">
        <span class="restart"></span>
        <span class="prog"></span>
        <span class="nav">
          <!-- Back to the intro when that is where we came from; otherwise out of the
               step, as before. Same condition as initialize() — without a previous
               evaluation there is no intro behind this step. -->
          <button
            type="button"
            class="lvl-btn lvl-btn--outlined"
            @click="!isNew && doc?.doc_id && hasPreviousEvaluation ? (viewState = 'reIntro') : $emit('back')"
          >Previous</button>
          <button type="button" class="lvl-btn" @click="skipUpload">
            {{ confirmMessage ?? 'Continue' }}
            <span class="ms">arrow_forward</span>
          </button>
        </span>
      </div>
    </div>

    <!-- Uploaded file confirmation. The extracted preview lives in the holder's
         shared right-hand column (as in later steps). -->
    <div v-else-if="viewState === 'preview'" class="lvl-qcard">
      <div class="lvl-qcard-in">
        <div class="lvl-dochead">
          <span class="lvl-welcome-ic"><span class="ms" style="font-size:24px">upload_file</span></span>
          <div class="lvl-dochead-tx">
            <h2>Add the document</h2>
            <p>Upload the job description. You&apos;ll have it to hand as you work through the role &mdash; and Atlas can highlight the passages relevant to each factor as you go.</p>
          </div>
        </div>

        <div class="lvl-drop has lvl-wc-docfile" :class="{ 'lvl-drop--dragging': isDragging }" role="button" tabindex="0"
            @click="openFilePicker" @keydown.enter="openFilePicker" @keydown.space.prevent="openFilePicker"
            @dragenter.prevent="onDragEnter" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
          <span class="ms">task</span>
          <div class="lvl-wc-docfile-name">{{ previewFileName }}</div>
          <button type="button" class="lvl-btn lvl-btn--outlined lvl-btn--sm">
            Replace
          </button>
        </div>

        <!-- Advisory JD readiness check (PD-1250). Hidden unless a readiness
             chain is configured for the client. -->
        <ReadinessPanel
          v-if="readinessChainId"
          :error="readiness.error.value"
          :summary="readiness.summary.value"
          :results="readiness.factorResults.value"
        >
          <!-- Is the advisory check actually worth running? (PD-1289) -->
          <template #feedback>
            <cUserFeedback
              feedback-type="LEVELLING_DOCUMENT_READINESS"
              :doc-id="doc?.doc_id"
              :file-id="previewFile?.file_id"
              :prompt-chain-id="readinessChainId"
            />
          </template>
        </ReadinessPanel>
      </div>

      <div class="lvl-cardfoot">
        <span class="restart"></span>
        <span class="prog"></span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--outlined" @click="$emit('back')">Previous</button>
          <button type="button" class="lvl-btn" @click="useThisFile">
            {{ confirmMessage ?? 'Continue' }}
            <span class="ms">arrow_forward</span>
          </button>
        </span>
      </div>
    </div>

    <!-- Optional upload step -->
    <div v-else-if="viewState === 'upload'" class="lvl-qcard">
      <div class="lvl-qcard-in">
        <div class="lvl-dochead">
          <span class="lvl-welcome-ic"><span class="ms" style="font-size:24px">upload_file</span></span>
          <div class="lvl-dochead-tx">
            <h2>Add the document</h2>
            <p>Upload the job description. You&apos;ll have it to hand as you work through the role &mdash; and Atlas can highlight the passages relevant to each factor as you go.
              <!-- You can skip this if you prefer. -->
            </p>
          </div>
        </div>

        <div class="lvl-wc-uprow">
          <div class="lvl-drop lvl-wc-docfile" :class="{ 'lvl-drop--dragging': isDragging }" role="button" tabindex="0"
              @click="openFilePicker" @keydown.enter="openFilePicker" @keydown.space.prevent="openFilePicker"
              @dragenter.prevent="onDragEnter" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
            <span class="ms">upload_file</span>
            <div class="lvl-wc-docfile-name">Drop a job description, or click to upload</div>
            <p>PDF or DOCX · Atlas extracts the responsibilities</p>
          </div>
          <!-- <span class="lvl-wc-or">Or</span>
          <button type="button" class="lvl-btn lvl-btn--outlined" @click="skipUpload">Skip this</button> -->
        </div>
      </div>

      <div class="lvl-cardfoot">
        <span class="restart"></span>
        <span class="prog"></span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--outlined" @click="$emit('back')">Previous</button>
        </span>
      </div>
    </div>

    <input ref="fileInput" type="file" class="lvl-visually-hidden" accept=".doc,.docx,.pdf"
        @change="onFileSelected" />
  </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>
<style scoped>
/* Hidden trigger: kept in the DOM (and programmatically clickable) but out of view. */
.lvl-visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }

/* loading */
.lvl-wc-loading { align-items: center; justify-content: center; gap: 14px; padding: 64px 26px; text-align: center; color: var(--v3-text-2); }
.lvl-wc-loading p { margin: 0; font-size: 15px; }
.lvl-wc-spin { color: var(--v3-accent); animation: lvl-wc-spin 1s linear infinite; }
@keyframes lvl-wc-spin { to { transform: rotate(360deg); } }

/* re-evaluate: current-level note + attached job-document card (the document
   itself previews in the holder's shared right-hand column). */
.lvl-wc-relevel { margin-top: 16px; }
.lvl-wc-docfile { margin-top: 16px; width: 100%; cursor: pointer; }
/* drag-over highlight for the drop zone */
.lvl-drop--dragging { border-color: var(--v3-accent); background: var(--v3-accent-weak, rgba(99, 102, 241, 0.08)); }
.lvl-drop--dragging * { pointer-events: none; }
.lvl-wc-docfile-name { font-size: 15px; font-weight: 650; color: var(--v3-text); overflow-wrap: anywhere; }

/* optional-upload row: uploader + "Or" divider + skip */
.lvl-wc-uprow { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; margin-top: 20px; }
.lvl-wc-or { font-size: 13px; font-weight: 600; color: var(--v3-text-3); }
</style>
